// 공지사항 관리 관련 타입 정의

// 레이아웃 타입
export type NoticeLayoutType = '1' | '2';
export type NoticeLayoutDisplayType = '1칸' | '2칸';

// 노출 상태 타입
export type NoticeExposureType = 'show' | 'hide';
export type NoticeExposureDisplayType = '노출' | '비노출';

// 승인 상태 타입
export type NoticeApprovalStatusType = 'pending' | 'approved' | 'rejected';
export type NoticeApprovalStatusDisplayType = '승인대기' | '승인완료' | '승인반려';

// 공지사항 내용 아이템
export interface NoticeContentItem {
  subtitle: string;
  detail: string;
}

// 사용자 정보 (관계형 데이터)
export interface NoticeUser {
  id: number;
  name: string;
  email: string;
}

// 공지사항 아이템 인터페이스
export interface NoticeItem {
  id: number;
  title: string;
  layout: NoticeLayoutType;
  exposure: NoticeExposureType;
  startDate: string; // DATEONLY format: YYYY-MM-DD
  endDate: string;   // DATEONLY format: YYYY-MM-DD
  contents: NoticeContentItem[];
  additionalInfo?: string;
  colorScheme?: string; // 컬러 스킴 키
  approvalStatus: NoticeApprovalStatusType;
  approvedAt?: string;
  rejectionReason?: string;
  createdBy: number;
  createdAt: string;
  updatedAt: string;
  // 관계형 데이터
  creator?: NoticeUser;
}

// 공지사항 목록 응답 인터페이스
export interface NoticeListResponse {
  success: boolean;
  data: {
    notices: NoticeItem[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
}

// 공지사항 상세 응답 인터페이스
export interface NoticeDetailResponse {
  success: boolean;
  data: NoticeItem;
}

// 공지사항 생성 요청 인터페이스
export interface CreateNoticeRequest {
  title: string;
  layout?: NoticeLayoutType;
  exposure?: NoticeExposureType;
  startDate: string;
  endDate: string;
  contents: NoticeContentItem[];
  additionalInfo?: string;
  colorScheme?: string; // 컬러 스킴 키
  approvalStatus?: NoticeApprovalStatusType;
}

// 공지사항 수정 요청 인터페이스
export interface UpdateNoticeRequest {
  id: number;
  title?: string;
  layout?: NoticeLayoutType;
  exposure?: NoticeExposureType;
  startDate?: string;
  endDate?: string;
  contents?: NoticeContentItem[];
  additionalInfo?: string;
  colorScheme?: string; // 컬러 스킴 키
  approvalStatus?: NoticeApprovalStatusType;
}

// 공지사항 승인 처리 요청 인터페이스
export interface ApproveNoticeRequest {
  id: number;
  approvalStatus: 'approved' | 'rejected';
}

// 공지사항 일괄 삭제 요청 인터페이스
export interface BulkDeleteNoticesRequest {
  ids: number[];
}

// 공지사항 일괄 노출 상태 변경 요청 인터페이스
export interface BulkUpdateNoticeExposureRequest {
  ids: number[];
  exposure: NoticeExposureType;
}

// 공지사항 필터 옵션
export interface NoticeFilterOptions {
  page?: number;
  limit?: number;
  search?: string;
  layout?: NoticeLayoutType | 'all';
  exposure?: NoticeExposureType | 'all';
  approvalStatus?: NoticeApprovalStatusType | 'all';
}

// 레이아웃 표시 텍스트 변환 함수
export const getLayoutDisplayText = (layout: NoticeLayoutType): NoticeLayoutDisplayType => {
  return layout === '1' ? '1칸' : '2칸';
};

export const getLayoutTypeFromDisplay = (displayType: NoticeLayoutDisplayType): NoticeLayoutType => {
  return displayType === '1칸' ? '1' : '2';
};

// 노출 상태 표시 텍스트 변환 함수
export const getExposureDisplayText = (exposureType: NoticeExposureType): NoticeExposureDisplayType => {
  return exposureType === 'show' ? '노출' : '비노출';
};

export const getExposureTypeFromDisplay = (displayType: NoticeExposureDisplayType): NoticeExposureType => {
  return displayType === '노출' ? 'show' : 'hide';
};

// 승인 상태 표시 텍스트 변환 함수
export const getApprovalStatusDisplayText = (status: NoticeApprovalStatusType): NoticeApprovalStatusDisplayType => {
  switch (status) {
    case 'pending':
      return '승인대기';
    case 'approved':
      return '승인완료';
    case 'rejected':
      return '승인반려';
    default:
      return '승인대기';
  }
};

export const getApprovalStatusFromDisplay = (displayType: NoticeApprovalStatusDisplayType): NoticeApprovalStatusType => {
  switch (displayType) {
    case '승인대기':
      return 'pending';
    case '승인완료':
      return 'approved';
    case '승인반려':
      return 'rejected';
    default:
      return 'pending';
  }
};

// 공지사항 활성화 상태 확인 함수
export const isNoticeActive = (notice: NoticeItem): boolean => {
  const now = new Date();
  const startDate = new Date(notice.startDate);
  const endDate = new Date(notice.endDate);

  return (
    notice.exposure === 'show' &&
    notice.approvalStatus === 'approved' &&
    now >= startDate &&
    now <= endDate
  );
};

// 공지사항 승인 가능 여부 확인 함수
export const canApproveNotice = (notice: NoticeItem): boolean => {
  return notice.approvalStatus === 'pending';
};